import { PRODUCT_NAME } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Badge, Field, Icon, Select, Skeleton, StatusBadge, cn } from "@sagot/ui";
import { useFormatters } from "../../shared";
import { automaticRegionCode, lastSyncCounts, type ZohoRegion, type ZohoStatus } from "../zoho-model";

export const ZOHO_I18N = "settings.integrations.zoho";
const NO_VALUE = "—";

/** Status = icon + text + colour; the badge carries `data-testid="zoho-status"`. */
export function ZohoStatusBadge({ state }: { state: ZohoStatus["state"] }) {
  const { t } = useTranslation();
  switch (state) {
    case "connected":
      return <StatusBadge status="success" label={t("settings.integrations.statusConnected")} data-testid="zoho-status" />;
    case "needs_reconnect":
      return <StatusBadge status="warning" label={t(`${ZOHO_I18N}.statusReconnect`)} data-testid="zoho-status" />;
    case "disconnected":
      return <StatusBadge status="neutral" label={t("settings.integrations.statusDisconnected")} data-testid="zoho-status" />;
    case "unavailable":
      return (
        <Badge data-testid="zoho-status">
          <Icon name="clock" className="size-3" />
          {t("settings.integrations.statusUnavailable")}
        </Badge>
      );
  }
}

const MESSAGE_KEY = {
  unavailable: "description",
  disconnected: "description",
  connected: "connectedBody",
  needs_reconnect: "reconnectBody",
} as const satisfies Record<ZohoStatus["state"], string>;

/** One sentence for the current state; the wrapper is a polite live region so a change is announced. */
function ZohoStatusMessage({ state }: { state: ZohoStatus["state"] }) {
  const { t } = useTranslation();
  return (
    <>
      <p className={cn("mt-0.5 text-xs", state === "needs_reconnect" ? "text-fg" : "text-fg-muted")}>
        {t(`${ZOHO_I18N}.${MESSAGE_KEY[state]}`, { product: PRODUCT_NAME })}
      </p>
      {state === "unavailable" ? <p className="mt-2 text-xs font-semibold text-fg-muted">{t(`${ZOHO_I18N}.unavailable`)}</p> : null}
    </>
  );
}

/** Icon, name, status badge and the state's message (`null` status while loading or failed). */
export function ZohoHeader({ status }: { status: ZohoStatus | null }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-start gap-3">
      <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg bg-sunken text-fg-muted">
        <Icon name="users" className="size-5" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 id="zoho-title" className="font-bold">{t(`${ZOHO_I18N}.name`)}</h3>
          {status ? <ZohoStatusBadge state={status.state} /> : null}
        </div>
        <div aria-live="polite">{status ? <ZohoStatusMessage state={status.state} /> : null}</div>
      </div>
    </div>
  );
}

function Detail({ label, children, testId }: { label: string; children: string; testId?: string }) {
  return (
    <div>
      <dt className="text-xs text-fg-muted">{label}</dt>
      <dd className="break-words font-semibold" data-testid={testId}>{children}</dd>
    </div>
  );
}

function LastSync({ status }: { status: ZohoStatus }) {
  const { t } = useTranslation();
  const { dateTime } = useFormatters();
  const counts = lastSyncCounts(status);
  const date = dateTime(status.last_sync_at);
  const text = !status.last_sync_at ? t(`${ZOHO_I18N}.details.noSync`) : counts ? t(`${ZOHO_I18N}.details.syncResult`, { date, ...counts }) : date;
  return <Detail label={t(`${ZOHO_I18N}.details.lastSync`)} testId="zoho-last-sync">{text}</Detail>;
}

/** The account that connected (it decides the data center), and the newest lead sync. */
export function ZohoDetails({ status }: { status: ZohoStatus }) {
  const { t } = useTranslation();
  return (
    <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 pl-13 text-sm sm:grid-cols-2" data-testid="zoho-details">
      <Detail label={t(`${ZOHO_I18N}.details.account`)}>{status.account_email ?? NO_VALUE}</Detail>
      <Detail label={t(`${ZOHO_I18N}.details.organization`)}>{status.org_name ?? NO_VALUE}</Detail>
      <Detail label={t(`${ZOHO_I18N}.details.datacenter`)}>{status.datacenter_label ?? status.datacenter ?? NO_VALUE}</Detail>
      <LastSync status={status} />
    </dl>
  );
}

interface RegionSelectProps {
  regions: readonly ZohoRegion[];
  /** Chosen region code; empty = the automatic one. */
  value: string;
  onChange: (code: string) => void;
  disabled: boolean;
}

export function ZohoRegionSelect({ regions, value, onChange, disabled }: RegionSelectProps) {
  const { t } = useTranslation();
  return (
    <Field label={t(`${ZOHO_I18N}.region`)} hint={t(`${ZOHO_I18N}.regionHint`)} className="mt-3 max-w-sm pl-13">
      <Select value={value || automaticRegionCode(regions)} disabled={disabled} data-testid="zoho-region" onChange={(event) => { onChange(event.target.value); }}>
        {regions.map((region) => (
          <option key={region.code} value={region.code}>
            {region.automatic ? t(`${ZOHO_I18N}.regionAutomatic`) : region.label}
          </option>
        ))}
      </Select>
    </Field>
  );
}

/** Shaped like the loaded card: icon, title + status, description, then the action row. */
export function ZohoCardSkeleton() {
  return (
    <article className="card card-body" data-testid="zoho-card-loading" aria-busy="true">
      <div className="flex items-start gap-3">
        <Skeleton className="size-10 shrink-0" />
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-5 w-[160px]" />
          <Skeleton className="h-4 w-full max-w-md" />
        </div>
      </div>
      <Skeleton className="ml-13 mt-3 h-8 w-[160px]" />
    </article>
  );
}
